import RightTimeApi from "@/API";
import Enums from "@/enums";

export type BillingOrgOption = { key: number; value: string };

export type BillingOrgDetails = {
    id: number;
    name: string;
    legalName?: string | null;
    remitToAddress?: string | null;
    remitToEmail?: string | null;
    taxId?: string | null;
    companyCount?: number;
    assignedUserIds?: number[];
    directorUserId?: number | null;
    directorName?: string;
    directorEmail?: string;
};

export type CompanyBillingIssue = {
    code: string;
    message: string;
    severity: "Error" | "Warning" | "Info" | string;
    companyTestTypeId?: number | null;
    companyTestTypeName?: string | null;
};

export type CompanyBillingConfiguration = {
    companyId: number;
    companyName: string;
    active: boolean;
    billable: boolean;
    billingOrgId: number;
    billingEmail?: string | null;
    isConfigured: boolean;
    errorCount: number;
    warningCount: number;
    issues: CompanyBillingIssue[];
};

export type BillingOrgDetail = {
    org: BillingOrgDetails;
    companies: CompanyBillingConfiguration[];
    activeCompanyCount: number;
    inactiveCompanyCount: number;
    configuredCompanyCount: number;
    misconfiguredCompanyCount: number;
};

export type AccountingBillingOrgContext = {
    billingOrgs: BillingOrgDetails[];
    accessibleBillingOrgIds: number[] | null;
    canManageBillingOrgs: boolean;
    canAccessGlobalAccounting: boolean;
};

export type AccountingUserOption = {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
};

type Permission = { companyId?: number; permissionId?: number };

export function hasSystemAdminPermission(
    permissions?: Permission[],
    adminPerms?: number[]
): boolean {
    if (adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin)) return true;
    return !!permissions?.some(
        (p) => Number(p.permissionId) === Enums.ROLE_PERMS.SystemAdmin
    );
}

export function normalizeBillingOrgs(raw?: unknown[]): BillingOrgOption[] {
    if (!raw?.length) return [];
    return raw
        .map((entry) => {
            const o = entry as Record<string, unknown>;
            const key = Number(o.key ?? o.id ?? o.Id);
            const value = String(o.value ?? o.name ?? o.Name ?? "");
            if (!key || !value) return null;
            return { key, value };
        })
        .filter((o): o is BillingOrgOption => o !== null);
}

export function appendBillingOrgQuery(url: string, billingOrgId?: number): string {
    if (!billingOrgId || billingOrgId <= 0) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}billingOrgId=${billingOrgId}`;
}

export function getBillingOrgIdFromUrl(): number {
    if (typeof window === "undefined") return 0;
    const value = new URLSearchParams(window.location.search).get("billingOrgId");
    const parsed = value ? parseInt(value, 10) : 0;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export async function loadBillingOrgOptionsAsync(): Promise<BillingOrgOption[]> {
    const resp = await RightTimeApi.getAsync("/BillingOrgs");
    if (resp?.ok && Array.isArray(resp.jsonData)) {
        return normalizeBillingOrgs(resp.jsonData);
    }
    return [];
}

export async function loadAccountingBillingOrgContextAsync(): Promise<AccountingBillingOrgContext | null> {
    const resp = await RightTimeApi.getAsync("/Accounting/BillingOrgContext");
    if (resp?.ok && resp.jsonData) {
        return resp.jsonData as AccountingBillingOrgContext;
    }
    return null;
}

export async function loadBillingOrgsDetailedAsync(): Promise<BillingOrgDetails[]> {
    const resp = await RightTimeApi.getAsync("/BillingOrgs?details=true");
    if (resp?.ok && Array.isArray(resp.jsonData)) {
        return resp.jsonData as BillingOrgDetails[];
    }
    return [];
}

export async function loadBillingOrgDetailAsync(billingOrgId: number): Promise<BillingOrgDetail | null> {
    const resp = await RightTimeApi.getAsync(`/BillingOrg/${billingOrgId}/Detail`);
    if (resp?.ok && resp.jsonData) {
        return resp.jsonData as BillingOrgDetail;
    }
    return null;
}

export async function loadCompanyBillingConfigurationAsync(companyId: number): Promise<CompanyBillingConfiguration | null> {
    const resp = await RightTimeApi.getAsync(`/Company/${companyId}/BillingConfiguration`);
    if (resp?.ok && resp.jsonData) {
        return resp.jsonData as CompanyBillingConfiguration;
    }
    return null;
}

export async function loadBillingOrgAccountingUsersAsync(): Promise<AccountingUserOption[]> {
    const resp = await RightTimeApi.getAsync("/BillingOrg/AccountingUsers");
    if (resp?.ok && Array.isArray(resp.jsonData)) {
        return resp.jsonData as AccountingUserOption[];
    }
    return [];
}

export async function enrichCompanyWithBillingOrgsAsync<T extends { permissions?: Permission[]; billingOrgs?: unknown[] }>(
    company: T,
    adminPerms?: number[]
): Promise<T & { billingOrgs: BillingOrgOption[] }> {
    if (!hasSystemAdminPermission(company.permissions, adminPerms)) {
        return { ...company, billingOrgs: normalizeBillingOrgs(company.billingOrgs) };
    }

    let billingOrgs = normalizeBillingOrgs(company.billingOrgs);
    if (billingOrgs.length === 0) {
        billingOrgs = await loadBillingOrgOptionsAsync();
    }
    return { ...company, billingOrgs };
}

export async function saveBillingOrgAsync(companyId: number, billingOrgId: number) {
    return RightTimeApi.putAsync(`/Company/${companyId}/BillingOrg`, { billingOrgId });
}

export async function saveBillingOrgDetailsAsync(org: BillingOrgDetails) {
    if (org.id) {
        return RightTimeApi.putAsync(`/BillingOrg/${org.id}`, org);
    }
    return RightTimeApi.postAsync("/BillingOrg", org);
}

export async function deleteBillingOrgAsync(id: number) {
    return RightTimeApi.deleteAsync(`/BillingOrg/${id}`);
}

export async function saveBillingOrgUsersAsync(billingOrgId: number, userIds: number[]) {
    return RightTimeApi.putAsync(`/BillingOrg/${billingOrgId}/Users`, { userIds });
}

export function companyBillingConfigStatusClass(company: CompanyBillingConfiguration): string {
    if (!company.active || !company.billable) return "billingConfigSkipped";
    if (company.isConfigured) return company.warningCount > 0 ? "billingConfigWarning" : "billingConfigOk";
    return "billingConfigError";
}

export function companyBillingConfigStatusLabel(company: CompanyBillingConfiguration): string {
    if (!company.active) return "Inactive";
    if (!company.billable) return "Not billable";
    if (company.isConfigured) return company.warningCount > 0 ? "Configured (warnings)" : "Configured";
    return "Needs attention";
}

export function formatAccountingUserName(user: AccountingUserOption): string {
    const name = `${user.firstName} ${user.lastName}`.trim();
    return user.email ? `${name} (${user.email})` : name;
}
